/**
 * Keeps `getManufacturerCatalogue()` honest across the gap between a write
 * landing in Firestore and the same write landing in Algolia.
 *
 * THE BUG THIS EXISTS FOR. The catalogue is served from
 * `watchkeeper_manufacturers`, which watchlock syncs from a Firestore trigger —
 * a second or two behind the write, sometimes more. Create a manufacturer from
 * the quick-create modal and every other surface that resolves a manufacturer
 * id to a name (the calibre listing, the module editor, the calibre pickers,
 * the movement panel) either never refetches at all — most gate their load on
 * `manufacturers.length > 0` — or refetches straight into an index that has not
 * seen the new record yet. Either way the manufacturer is missing until a full
 * page reload, which is exactly what James reported.
 *
 * THE FIX, in two halves, both of which live here:
 *
 * 1. An OVERLAY of locally-known writes, applied to every catalogue fetch. A
 *    manufacturer created in this tab is present in the returned list from the
 *    moment `createMovementManufacturer` resolves, whatever Algolia thinks. It
 *    is not a guess: the Firestore write has already succeeded, so the entry is
 *    a real document. The overlay drops each entry as soon as a fetch returns
 *    it, so it is empty again within a couple of seconds and cannot go stale.
 *
 * 2. A CHANGE NOTIFICATION, so surfaces mounted elsewhere in the app refetch
 *    rather than waiting for a remount. With the overlay behind it, that
 *    refetch is correct immediately — there is no need for the caller to guess
 *    an indexing delay and schedule a second, later fetch.
 *
 * Deliberately in-process and unpersisted. A reload reads Firestore/Algolia
 * fresh, and by then the trigger has long since run.
 */

import type { ManufacturerCatalogueEntry } from './manufacturerCatalogueQuery';

/**
 * Manufacturers written here but not yet seen coming back from a fetch.
 *
 * Keyed by doc id, so a create followed by an edit before the index catches up
 * leaves one entry, not two.
 */
const pendingCreates = new Map<string, ManufacturerCatalogueEntry>();

/**
 * Manufacturers deleted here but possibly still in the index.
 *
 * The mirror of `pendingCreates`, and needed for the same reason: Algolia keeps
 * serving a deleted record until the delete trigger runs, so a list refetched
 * immediately after a delete would show the row again. Cleared per id once a
 * fetch stops returning it.
 */
const pendingDeletes = new Set<string>();

/** Listeners wanting to know the catalogue's MEMBERSHIP changed. */
const listeners = new Set<() => void>();

/**
 * Note a manufacturer this client just created.
 *
 * Takes the projection rather than the whole document because that is what the
 * catalogue serves — see `ManufacturerCatalogueEntry` — so an overlaid entry is
 * indistinguishable from a fetched one and no consumer needs to know which it
 * got.
 */
export function recordCreatedManufacturer(
  entry: ManufacturerCatalogueEntry,
): void {
  pendingDeletes.delete(entry.id);
  pendingCreates.set(entry.id, entry);
}

/** Note a manufacturer this client just deleted. */
export function recordDeletedManufacturer(id: string): void {
  pendingCreates.delete(id);
  pendingDeletes.add(id);
}

/**
 * Reconcile a freshly fetched catalogue against the overlay, and return what the
 * caller should actually see.
 *
 * PURE except for the reconciliation it performs on the overlay itself, which is
 * the point: a fetch that already contains a pending create means the index has
 * caught up, so the entry is no longer needed and is dropped. Same for a delete
 * the fetch no longer returns. The overlay therefore drains itself without a
 * timer, and an entry can only linger while it is still genuinely needed.
 *
 * Appends rather than inserts, then re-sorts by name, because every consumer
 * either renders the list in the order given or re-sorts on its own label. See
 * `sortManufacturersByName`, whose ordering this reproduces.
 */
export function applyManufacturerOverlay(
  fetched: ManufacturerCatalogueEntry[],
): ManufacturerCatalogueEntry[] {
  if (pendingCreates.size === 0 && pendingDeletes.size === 0) return fetched;

  const fetchedIds = new Set(fetched.map((m) => m.id));

  // Reconcile: anything the source now agrees about needs no overlay.
  for (const id of pendingCreates.keys()) {
    if (fetchedIds.has(id)) pendingCreates.delete(id);
  }
  for (const id of pendingDeletes) {
    if (!fetchedIds.has(id)) pendingDeletes.delete(id);
  }

  if (pendingCreates.size === 0 && pendingDeletes.size === 0) return fetched;

  const result = fetched.filter((m) => !pendingDeletes.has(m.id));
  for (const entry of pendingCreates.values()) result.push(entry);
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Subscribe to catalogue membership changes. Returns the unsubscribe.
 *
 * Fired on create and delete only — the two things that change WHICH
 * manufacturers exist, and so the two that leave another surface's dropdown
 * wrong. A field edit leaves every list's membership correct and is already
 * refreshed by the surface that made it, so notifying on updates would cost
 * every mounted consumer a fetch to change nothing.
 */
export function subscribeManufacturerCatalogue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tell every mounted consumer to refetch. */
export function notifyManufacturerCatalogueChanged(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch (err) {
      // One consumer's bad reducer must not stop the rest from refreshing.
      console.warn('[manufacturers] catalogue listener threw', err);
    }
  }
}

/** Test seam — the overlay is module state and tests must not leak into each other. */
export function __resetManufacturerOverlay(): void {
  pendingCreates.clear();
  pendingDeletes.clear();
  listeners.clear();
}
